import { useEffect, useRef, useState } from "react";
import { Copy, Check, ExternalLink, Sparkles, Code2, Zap, Shield, ArrowLeft } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import SeoHead from "./SeoHead";

const SITE = typeof window !== "undefined" ? window.location.origin : "https://vatcalc.eu";

const SNIPPETS = {
    basic: `<!-- VATcalc.eu validity badge -->
<div data-vatcalc-badge data-vat="DE811569869"></div>
<script src="${SITE}/badge.js" async></script>`,
    dark: `<div data-vatcalc-badge data-vat="DE811569869" data-theme="dark"></div>
<script src="${SITE}/badge.js" async></script>`,
    small: `<div data-vatcalc-badge data-vat="DE811569869" data-size="sm"></div>
<script src="${SITE}/badge.js" async></script>`,
    custom: `<div
  data-vatcalc-badge
  data-vat="FR40303265045"
  data-theme="light"
  data-size="lg"
  data-label="VAT verified">
</div>
<script src="${SITE}/badge.js" async></script>`,
};

function CopyButton({ text, testId }) {
    const [copied, setCopied] = useState(false);
    return (
        <button
            data-testid={testId}
            onClick={async () => {
                try {
                    await navigator.clipboard.writeText(text);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1800);
                } catch (err) {
                    console.warn("[VATcalc] Clipboard copy failed:", err);
                }
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-800 px-2.5 py-1.5 text-xs font-medium text-slate-200 hover:bg-slate-700 transition-colors"
        >
            {copied ? (
                <>
                    <Check className="h-3.5 w-3.5 text-emerald-400" /> Copied
                </>
            ) : (
                <>
                    <Copy className="h-3.5 w-3.5" /> Copy
                </>
            )}
        </button>
    );
}

function CodeBlock({ code, testId }) {
    return (
        <div className="relative rounded-xl bg-slate-900 border border-slate-800 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800 bg-slate-950/50">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Code2 className="h-3.5 w-3.5" /> HTML
                </div>
                <CopyButton text={code} testId={`${testId}-copy`} />
            </div>
            <pre className="p-4 overflow-x-auto text-[13px] leading-relaxed text-slate-100">
                <code data-testid={testId}>{code}</code>
            </pre>
        </div>
    );
}

/**
 * Renders a live badge by mounting a host element and invoking window.VATcalcBadge.mount().
 * The badge.js script is loaded once globally below.
 */
function LiveBadge({ vat, theme = "light", size = "md", label, testId }) {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        // wait for script
        const tryMount = () => {
            if (window.VATcalcBadge && window.VATcalcBadge.mount) {
                // reset
                el.__vcInit = false;
                el.innerHTML = "";
                el.removeAttribute("data-vatcalc-badge");
                window.VATcalcBadge.mount(el, { vat, theme, size, ...(label ? { label } : {}) });
                if (label) el.setAttribute("data-label", label);
            } else {
                setTimeout(tryMount, 120);
            }
        };
        tryMount();
    }, [vat, theme, size, label]);
    return <div ref={ref} data-testid={testId} />;
}

export default function BadgePage() {
    const [demoVat, setDemoVat] = useState("DE811569869");
    const [demoTheme, setDemoTheme] = useState("light");
    const [demoSize, setDemoSize] = useState("md");
    const [previewKey, setPreviewKey] = useState(0);

    // Inject /badge.js once. We point the API at REACT_APP_BACKEND_URL so the live demo
    // works in any environment (dev/preview/prod) where backend and frontend may be on
    // separate origins. Real third-party embeds don't set this global and use the
    // script's own origin — which on vatcalc.eu serves both frontend and API.
    useEffect(() => {
        if (document.getElementById("vatcalc-badge-js")) return;
        const apiOrigin = process.env.REACT_APP_BACKEND_URL;
        if (apiOrigin) window.__VATCALC_API_ORIGIN = apiOrigin;
        window.__VATCALC_SITE_ORIGIN = window.location.origin;
        const s = document.createElement("script");
        s.id = "vatcalc-badge-js";
        s.src = "/badge.js";
        s.async = true;
        document.body.appendChild(s);
    }, []);

    const liveSnippet = `<div data-vatcalc-badge
  data-vat="${demoVat}"
  data-theme="${demoTheme}"
  data-size="${demoSize}"></div>
<script src="${SITE}/badge.js" async></script>`;

    return (
        <div className="min-h-screen bg-white text-slate-900" data-testid="badge-page">
            <SeoHead
                title="Free Embeddable EU VAT Badge — Verify VAT Numbers Instantly | VATcalc.eu"
                description="Add a one-line VAT validity badge to your checkout, footer or invoice. Verifies any EU VAT number against the official VIES service. Free, no signup, copy-paste install."
                canonicalPath="/badge"
            />
            <Header />

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-slate-200">
                <div className="absolute inset-0 -z-10 bg-gradient-to-br from-emerald-50 via-white to-white" />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
                    <a
                        href="/"
                        data-testid="back-to-home"
                        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> Back to calculator
                    </a>
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                        <div className="lg:col-span-7">
                            <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-xs font-semibold tracking-wide">
                                <Sparkles className="h-3.5 w-3.5" /> NEW · Free for everyone
                            </div>
                            <h1 className="mt-4 font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                                Show customers your{" "}
                                <span className="text-[#10B981]">VAT is verified</span>{" "}
                                — in one line of HTML.
                            </h1>
                            <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-2xl">
                                Drop the VATcalc.eu badge into your checkout, invoice or footer.
                                It validates any EU VAT number against the official{" "}
                                <span className="font-medium text-slate-900">EU VIES</span> service
                                in real time. Free forever, no signup, no tracking.
                            </p>

                            <div className="mt-8 flex flex-wrap items-center gap-3">
                                <LiveBadge vat="DE811569869" size="lg" testId="hero-badge-de" />
                                <LiveBadge vat="FR40303265045" size="lg" testId="hero-badge-fr" />
                                <LiveBadge vat="BE0203201340" size="lg" testId="hero-badge-be" />
                            </div>

                            <div className="mt-8 flex flex-wrap gap-6 text-sm text-slate-600">
                                <span className="inline-flex items-center gap-1.5"><Zap className="h-4 w-4 text-emerald-600" /> &lt; 4 KB, no dependencies</span>
                                <span className="inline-flex items-center gap-1.5"><Shield className="h-4 w-4 text-emerald-600" /> Official EU VIES data</span>
                                <span className="inline-flex items-center gap-1.5"><Code2 className="h-4 w-4 text-emerald-600" /> Works on any site</span>
                            </div>
                        </div>

                        <div className="lg:col-span-5">
                            <div className="rounded-2xl border border-slate-200 bg-white shadow-xl shadow-emerald-900/5 p-5">
                                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
                                    Quickstart
                                </div>
                                <CodeBlock code={SNIPPETS.basic} testId="hero-snippet" />
                                <p className="mt-3 text-xs text-slate-500">
                                    Paste anywhere in your HTML. The script auto-renders every
                                    <code className="px-1 mx-1 rounded bg-slate-100 text-slate-800 text-[11px]">data-vatcalc-badge</code> element.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Live builder */}
            <section className="border-b border-slate-200 bg-slate-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
                    <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Try it live</h2>
                    <p className="mt-2 text-slate-600">Configure, preview, copy the snippet.</p>

                    <div className="mt-8 grid lg:grid-cols-2 gap-6">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6">
                            <div className="space-y-5">
                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                                        VAT number
                                    </label>
                                    <input
                                        data-testid="demo-vat-input"
                                        value={demoVat}
                                        onChange={(e) => setDemoVat(e.target.value.toUpperCase())}
                                        onBlur={() => setPreviewKey((k) => k + 1)}
                                        onKeyDown={(e) => { if (e.key === "Enter") setPreviewKey((k) => k + 1); }}
                                        placeholder="DE811569869"
                                        className="w-full font-mono rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    />
                                    <p className="mt-1.5 text-xs text-slate-500">
                                        Try valid EU VATs: <button onClick={() => { setDemoVat("DE811569869"); setPreviewKey(k=>k+1); }} className="underline hover:text-emerald-600">DE811569869</button>,{" "}
                                        <button onClick={() => { setDemoVat("FR40303265045"); setPreviewKey(k=>k+1); }} className="underline hover:text-emerald-600">FR40303265045</button>,{" "}
                                        <button onClick={() => { setDemoVat("BE0203201340"); setPreviewKey(k=>k+1); }} className="underline hover:text-emerald-600">BE0203201340</button>,{" "}
                                        <button onClick={() => { setDemoVat("IT00159560366"); setPreviewKey(k=>k+1); }} className="underline hover:text-emerald-600">IT00159560366</button>
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Theme</label>
                                        <div className="inline-flex rounded-md border border-slate-300 p-0.5 bg-white">
                                            {["light", "dark"].map((th) => (
                                                <button
                                                    key={th}
                                                    data-testid={`theme-${th}`}
                                                    onClick={() => setDemoTheme(th)}
                                                    className={`px-3 py-1.5 text-xs font-medium rounded ${demoTheme === th ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                                                >{th}</button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Size</label>
                                        <div className="inline-flex rounded-md border border-slate-300 p-0.5 bg-white">
                                            {["sm", "md", "lg"].map((sz) => (
                                                <button
                                                    key={sz}
                                                    data-testid={`size-${sz}`}
                                                    onClick={() => setDemoSize(sz)}
                                                    className={`px-3 py-1.5 text-xs font-medium rounded ${demoSize === sz ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                                                >{sz}</button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-col">
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Preview</div>
                            <div
                                className={`flex-1 flex items-center justify-center rounded-xl p-8 ${demoTheme === "dark" ? "bg-slate-900" : "bg-slate-50"}`}
                            >
                                <LiveBadge
                                    key={previewKey + demoTheme + demoSize}
                                    vat={demoVat}
                                    theme={demoTheme}
                                    size={demoSize}
                                    testId="live-preview"
                                />
                            </div>
                            <div className="mt-4">
                                <CodeBlock code={liveSnippet} testId="live-snippet" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Options reference */}
            <section className="border-b border-slate-200">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
                    <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Options</h2>
                    <p className="mt-2 text-slate-600">All attributes are set on the target <code className="rounded bg-slate-100 px-1.5 py-0.5 text-sm">&lt;div&gt;</code>.</p>

                    <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200">
                        <table className="w-full text-sm" data-testid="options-table">
                            <thead className="bg-slate-50 text-slate-700">
                                <tr>
                                    <th className="text-left font-semibold px-5 py-3">Attribute</th>
                                    <th className="text-left font-semibold px-5 py-3">Values</th>
                                    <th className="text-left font-semibold px-5 py-3">Description</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                <tr>
                                    <td className="px-5 py-3 font-mono text-emerald-700">data-vat</td>
                                    <td className="px-5 py-3 font-mono text-slate-600">DE123456789</td>
                                    <td className="px-5 py-3 text-slate-700">EU VAT number with 2-letter country prefix. <span className="text-slate-500">Required.</span></td>
                                </tr>
                                <tr>
                                    <td className="px-5 py-3 font-mono text-emerald-700">data-theme</td>
                                    <td className="px-5 py-3 font-mono text-slate-600">light <span className="text-slate-400">|</span> dark</td>
                                    <td className="px-5 py-3 text-slate-700">Color theme. Default <code className="text-xs bg-slate-100 px-1 rounded">light</code>.</td>
                                </tr>
                                <tr>
                                    <td className="px-5 py-3 font-mono text-emerald-700">data-size</td>
                                    <td className="px-5 py-3 font-mono text-slate-600">sm <span className="text-slate-400">|</span> md <span className="text-slate-400">|</span> lg</td>
                                    <td className="px-5 py-3 text-slate-700">Visual size. Default <code className="text-xs bg-slate-100 px-1 rounded">md</code>.</td>
                                </tr>
                                <tr>
                                    <td className="px-5 py-3 font-mono text-emerald-700">data-label</td>
                                    <td className="px-5 py-3 font-mono text-slate-600">"VAT verified"</td>
                                    <td className="px-5 py-3 text-slate-700">Optional override for the success label.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-10 grid md:grid-cols-2 gap-6">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Dark theme</div>
                            <div className="rounded-xl bg-slate-900 p-6 flex items-center justify-center">
                                <LiveBadge vat="DE811569869" theme="dark" size="md" testId="example-dark" />
                            </div>
                            <div className="mt-3"><CodeBlock code={SNIPPETS.dark} testId="snippet-dark" /></div>
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Small size</div>
                            <div className="rounded-xl bg-slate-50 p-6 flex items-center justify-center">
                                <LiveBadge vat="DE811569869" theme="light" size="sm" testId="example-sm" />
                            </div>
                            <div className="mt-3"><CodeBlock code={SNIPPETS.small} testId="snippet-sm" /></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why */}
            <section className="border-b border-slate-200 bg-slate-50">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
                    <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Why add a VAT badge?</h2>
                    <div className="mt-8 grid md:grid-cols-3 gap-6">
                        {[
                            { icon: Shield, title: "Build buyer trust", body: "Show your VAT registration is real, in real time. Reduces drop-off at checkout and signals legitimacy on invoices." },
                            { icon: Zap, title: "Zero maintenance", body: "Cached for 24h per VAT in the browser. No backend to set up, no API key. We absorb the VIES rate limits." },
                            { icon: ExternalLink, title: "Free, open & light", body: "Under 4 KB. Renders inside a Shadow DOM, so it can never break your site's styles. MIT-style attribution back to VATcalc.eu." },
                        ].map((f) => (
                            <div key={f.title} className="rounded-2xl border border-slate-200 bg-white p-6">
                                <f.icon className="h-5 w-5 text-emerald-600" />
                                <div className="mt-3 font-display font-bold text-lg">{f.title}</div>
                                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{f.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Advanced */}
            <section>
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
                    <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Advanced</h2>
                    <p className="mt-2 text-slate-600">For SPAs (React, Vue, Svelte) where badges mount dynamically:</p>

                    <div className="mt-6"><CodeBlock testId="snippet-spa" code={`<script src="${SITE}/badge.js" async></script>
<script>
  // After your framework injects a badge container:
  window.VATcalcBadge.mount(document.querySelector("#my-vat"), {
    vat: "DE811569869",
    theme: "light",
    size: "md"
  });
  // …or re-scan the whole DOM:
  window.VATcalcBadge.scan();
</script>`} /></div>

                    <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6">
                        <h3 className="font-display font-bold text-xl">Full customization example</h3>
                        <div className="mt-4"><CodeBlock testId="snippet-custom" code={SNIPPETS.custom} /></div>
                        <div className="mt-4 rounded-xl bg-slate-50 p-6 flex items-center justify-center">
                            <LiveBadge vat="FR40303265045" theme="light" size="lg" label="VAT verified" testId="example-custom" />
                        </div>
                    </div>

                    <div className="mt-10 text-sm text-slate-500">
                        Questions? <a href="/#faq" className="underline text-slate-700 hover:text-emerald-600">Check the FAQ</a> or
                        {" "}<a href="/" className="underline text-slate-700 hover:text-emerald-600">use the full VAT calculator</a>.
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
